<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { Plus, Eye, EyeOff } from 'lucide-vue-next';
// shadcn-vue 컴포넌트
import { Input } from '@/components/shadcnui/input';
import { Label } from '@/components/shadcnui/label';
import { Button } from '@/components/shadcnui/button';
import { Skeleton } from '@/components/shadcnui/skeleton';
import { Textarea } from '@/components/shadcnui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/shadcnui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/shadcnui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/shadcnui/dialog';
// store / composable
import { useRSStore } from '@/store/useRSStore';
import { useUserStore } from '@/store/useUserStore';
import { useUser } from '@/composable/useUser';
import { useRole } from '@/composable/useRole';
// 타입
import type { user } from '@/types/types';

const RSStore = useRSStore();
const UserStore = useUserStore();
const { onReadUserList, onAddUser, onUpdateUser, onDeleteUser } = useUser();
const { onReadRoleList } = useRole();

// 상태
const isLoading = ref(true);
const isDialogOpen = ref(false);
const isUpdateMode = ref(false);
const duplicateAccount = ref(false);

// 비밀번호 보기: 클릭 시 토글(잠금), 호버 시 일시 표시
const showPassword = ref(false);
const peekPassword = ref(false);
const isPasswordVisible = computed(() => showPassword.value || peekPassword.value);
//기존 사용자 정보를 담는 객체 (업데이트 모드에서 사용)
const existingAccount = ref();
const isFormInvalid = computed(() => {
  return !form.value.account.trim() || !form.value.password.trim() || form.value.role === 0;
});

const form = ref({
  account: '',
  password: '',
  description: '',
  role: 3,
});


// 다이얼로그 핸들러
const resetForm = () => {
  form.value = { account: '', password: '', description: '', role: 0 };
};

// 추가 버튼 클릭 시 폼 초기화 후 다이얼로그 열기
const openAddDialog = () => {
  isUpdateMode.value = false;
  resetForm();
  isDialogOpen.value = true;
};
const openUpdateDialog = (target: user) => {
  isUpdateMode.value = true;
  existingAccount.value = target.account; // 기존 계정 정보 저장
  form.value = {
    account: target.account,
    password: target.password,
    description: target.description,
    role: target.role,
  };
  isDialogOpen.value = true;
};

// ADD/UPDATE/DELETE API 호출 핸들러 
const handleAdd = async () => {
  console.log("추가할 사용자 데이터:", {
    account: form.value.account,
    password: form.value.password,
    description: form.value.description,
    role: form.value.role,
  });
  const result = await onAddUser(form.value.account, form.value.password, form.value.description, form.value.role);
  if (result === 'duplicate') {
    duplicateAccount.value = true;
    return;
  }
  isDialogOpen.value = false;
};
const handleUpdate = async () => {
  await onUpdateUser(existingAccount.value, form.value.account, form.value.password, form.value.description, form.value.role);
  isDialogOpen.value = false;
};
const handleDelete = async () => {
  await onDeleteUser(form.value.account);
  isDialogOpen.value = false;
};

const handleSubmit = () => (isUpdateMode.value ? handleUpdate() : handleAdd());

// 계정 입력값 변경 시 중복 체크 초기화
watch(() => form.value.account, () => {
    duplicateAccount.value = false; 
});

// 마운트 시 데이터 로드
onMounted(async () => {
  await onReadRoleList();
  await onReadUserList().finally(() => {
    isLoading.value = false;
  });
});
</script>

<template>
  <div class="p-8">
    <!-- 헤더 -->
    <div class="flex items-center justify-between mb-6">
      <h1 class="text-3xl font-bold text-foreground">유저관리</h1>
      <Button @click="openAddDialog">
        <Plus class="mr-2 h-4 w-4" />
        Add
      </Button>
    </div>

    <!-- 데이터 테이블 -->
    <div class="border rounded-lg bg-card overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow class="bg-muted/50 hover:bg-muted/50">
            <TableHead class="font-semibold px-5">ID</TableHead>
            <TableHead class="font-semibold ">Role</TableHead>
            <TableHead class="font-semibold ">Description</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <!-- 로딩 상태 -->
          <template v-if="isLoading">
            <TableRow v-for="i in UserStore.itemPerPage" :key="i">
              <TableCell colspan="3" class="px-3">
                <Skeleton class="h-5" />
              </TableCell>
            </TableRow>
          </template>
          <!-- 데이터 목록 -->
          <template v-else>
            <TableRow v-for="user in RSStore.userList" :key="user.account"
              class="cursor-pointer hover:bg-accent/50" @click="openUpdateDialog(user)">
              <TableCell class="px-5">{{ user.account }}</TableCell>
              <TableCell class="font-medium text-foreground">{{ RSStore.roleList.find(role => role.id === user.role)?.name || 'Unknown' }}</TableCell>
              <TableCell class="px-5">{{ user.description }}</TableCell>
            </TableRow>
            <TableRow v-for="i in Math.max(0, UserStore.itemPerPage - RSStore.userList.length)"
              :key="'empty-' + i">
              <TableCell colspan="3">&nbsp;</TableCell>
            </TableRow>
          </template>
        </TableBody>
      </Table>
    </div>

    <!-- 추가 / 수정 공용 다이얼로그 -->
    <Dialog v-model:open="isDialogOpen">
      <DialogContent class="sm:max-w-[800px]">
        <DialogHeader>
          <DialogTitle>{{ isUpdateMode ? '유저 정보 수정' : '유저 정보 추가' }}</DialogTitle>
          <DialogDescription />
        </DialogHeader>

        <div class="py-4 space-y-4 text-foreground">
          <div class="grid grid-cols-2 gap-4">
            <div>
              <Label class="text-sm text-muted-foreground">Account</Label>
              <Input class="text-sm font-medium "v-model="form.account"  maxlength="32"/>
              <p v-if="duplicateAccount" class="text-sm text-destructive mt-1">이미 존재하는 계정입니다.</p>
            </div>
            <div>
              <Label class="text-sm text-muted-foreground">Role</Label>
              <Select v-model="form.role" >
                <SelectTrigger class="">
                  <SelectValue placeholder="역할 선택" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem v-for="role in RSStore.roleList" :value="role.id" :key="role.id">
                    {{ role.name }}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div class="grid grid-cols-2 gap-4">
            <div >
              <Label class="text-sm text-muted-foreground">Password</Label>
              <div class="relative">
                <Input class="text-sm font-medium pr-10" v-model="form.password"
                  :type="isPasswordVisible ? 'text' : 'password'"  maxlength="255"/>
                <button type="button" tabindex="-1"
                  class="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  @click="showPassword = !showPassword"
                  @mouseenter="peekPassword = true"
                  @mouseleave="peekPassword = false">
                  <component :is="isPasswordVisible ? EyeOff : Eye" class="h-4 w-4" />
                </button>
              </div>
            </div>
           
          </div>
            <div >
              <Label class="text-sm text-muted-foreground">Description</Label>
              <Textarea class="text-sm font-medium w-full min-h-[100px]" v-model="form.description" />
            </div>
        </div>

        <DialogFooter class="gap-2">
          <Button @click="handleSubmit" :disabled="isFormInvalid">
            {{ isUpdateMode ? 'Update' : 'Add' }}
          </Button>
          <Button v-if="isUpdateMode" variant="destructive" @click="handleDelete">
            Delete
          </Button>
          <Button variant="outline" @click="isDialogOpen = false">
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </div>
</template>
